import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { ApiError, api } from "../../api";
import type { IssueDetail, IssueRefInfo, IssuesMeta } from "../../types";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../ui/breadcrumb";
import { Button } from "../ui/button";
import { DraftAttachments, type StagedAttachment, uploadIssueFile } from "./attachments";
import { IssueTitleField } from "./issue-editor";
import { MarkdownEditor } from "./markdown-editor";
import { PropertiesSidebar } from "./properties-sidebar";
import { VoiceIssueButton } from "./voice-issue";

export type Draft = {
  title: string;
  description: string;
  typeKey: string;
  statusKey: string;
  /** Null until the user picks one: a sub-issue takes its parent's repo, else the default. */
  repoKey: string | null;
  priority: number;
  storyPoints: number | null;
  reviewerId: string | null;
  reporterId: string | null;
  /** Null until the user picks teams, so the server can auto-assign. */
  teamIds: string[] | null;
  labelIds: string[];
  parent: string | null;
};

type IssuePatch = Parameters<typeof api.updateIssue>[1];

export function DraftIssue({
  draft,
  setDraft,
  meta,
  parent,
  createdAt,
}: {
  draft: Draft;
  setDraft: (next: Draft | ((current: Draft | null) => Draft | null)) => void;
  meta: IssuesMeta;
  parent: string | null;
  createdAt: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<"idle" | "creating" | "uploading">("idle");
  const [uploadingName, setUploadingName] = useState<string | null>(null);
  const [files, setFiles] = useState<StagedAttachment[]>([]);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const filesRef = useRef(files);
  filesRef.current = files;
  const titleRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    titleRef.current?.focus();
  }, []);
  const latest = useRef(draft);
  latest.current = draft;
  const savingRef = useRef(false);
  const mounted = useRef(true);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  const saveRef = useRef<() => Promise<void>>(async () => undefined);
  saveRef.current = saveDraft;
  const voiceOpenRef = useRef(false);
  voiceOpenRef.current = voiceOpen;

  const parentQuery = useQuery({
    queryKey: ["issue", parent],
    queryFn: () => api.issue(parent!),
    enabled: Boolean(parent),
  });
  const preview = useQuery({
    queryKey: ["team-preview", draft.reporterId, draft.reviewerId],
    queryFn: () =>
      api.issueTeamPreview({
        ...(draft.reporterId ? { reporterId: draft.reporterId } : {}),
        ...(draft.reviewerId ? { reviewerId: draft.reviewerId } : {}),
      }),
    enabled: draft.teamIds === null,
  });

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Enter" || event.repeat || !(event.metaKey || event.ctrlKey)) return;
      if (voiceOpenRef.current) return;
      event.preventDefault();
      void saveRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  async function saveDraft() {
    const snapshot = latest.current;
    if (!snapshot.title.trim() || savingRef.current) return;
    savingRef.current = true;
    if (mounted.current) setPhase("creating");
    try {
      const { issue } = await api.createIssue(toInput(snapshot));
      const staged = filesRef.current;
      const uploaded = [];
      if (staged.length && mounted.current) setPhase("uploading");
      for (const item of staged) {
        if (mounted.current) setUploadingName(item.file.name);
        try {
          uploaded.push(await uploadIssueFile(issue.identifier, item.file));
        } catch (error) {
          toast.error(error instanceof Error ? error.message : `Could not upload ${item.file.name}`);
        }
      }
      queryClientRef.current.setQueryData(["issue", issue.identifier], {
        issue: { ...issue, attachments: uploaded.length ? uploaded : issue.attachments },
      });
      void queryClientRef.current.invalidateQueries({ queryKey: ["issues"] });
      if (mounted.current) navigateRef.current(`/issues/${issue.identifier}`, { replace: true });
    } catch (error) {
      if (mounted.current) {
        toast.error(error instanceof ApiError ? error.message : "Could not save issue");
      }
    } finally {
      savingRef.current = false;
      if (mounted.current) {
        setPhase("idle");
        setUploadingName(null);
      }
    }
  }

  const teamIds = draft.teamIds ?? preview.data?.teamIds ?? [];
  const teams = teamIds.flatMap((id) => {
    const team = meta.teams.find((item) => item.id === id);
    return team ? [team] : [];
  });
  const parentIssue: IssueRefInfo | null = parent
    ? {
        id: parentQuery.data?.issue.id ?? parent,
        identifier: parentQuery.data?.issue.identifier ?? parent,
        title: parentQuery.data?.issue.title ?? "",
        statusKey: parentQuery.data?.issue.statusKey ?? "",
      }
    : null;
  const repoKey =
    draft.repoKey ??
    parentQuery.data?.issue.repoKey ??
    meta.repos.find((repo) => repo.isDefault)?.key ??
    meta.repos[0]?.key ??
    "";
  const issue = toIssue(draft, meta, teams, parentIssue, createdAt, repoKey);
  const canSave = draft.title.trim().length > 0 && phase === "idle";
  const saveLabel =
    phase === "uploading" ? "Uploading…" : phase === "creating" ? "Creating…" : "Create issue";

  return (
    <div className="mx-auto grid w-full max-w-9xl items-start gap-6 xl:grid-cols-[minmax(0,1fr)_21rem] xl:gap-x-8">
      <div className="fx-rise flex min-w-0 flex-col gap-7">
        <div className="flex items-center justify-between gap-3">
          <Breadcrumb className="text-xs">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/issues">Issues</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {parentIssue && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to={`/issues/${parentIssue.identifier}`}>{parentIssue.identifier}</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </>
              )}
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>New issue</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="flex items-center gap-1.5">
            <VoiceIssueButton
              disabled={phase !== "idle"}
              onOpenChange={setVoiceOpen}
              onCreate={async (spoken) => {
                const snapshot = { ...latest.current, ...spoken };
                latest.current = snapshot;
                setDraft(snapshot);
                await saveDraft();
              }}
            />
            <Button size="sm" disabled={!canSave} onClick={() => void saveDraft()}>
              {saveLabel}
            </Button>
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-5">
          <IssueTitleField
            fieldRef={titleRef}
            value={draft.title}
            onChange={(title) => setDraft((current) => (current ? { ...current, title } : current))}
          />
          <MarkdownEditor
            title="Description"
            initial=""
            value={draft.description}
            onChange={(description) =>
              setDraft((current) => (current ? { ...current, description } : current))
            }
          />
          <DraftAttachments
            files={files}
            onChange={setFiles}
            uploading={uploadingName}
            disabled={phase !== "idle"}
          />
        </div>
      </div>
      <PropertiesSidebar
        draft
        issue={issue}
        meta={meta}
        update={(patch) => setDraft((current) => (current ? applyPatch(current, patch) : current))}
        onChanged={() => undefined}
        onArchive={() => undefined}
        archivePending={false}
      />
    </div>
  );
}

function applyPatch(draft: Draft, patch: IssuePatch): Draft {
  return {
    ...draft,
    ...(patch.title !== undefined ? { title: patch.title } : {}),
    ...(patch.description !== undefined ? { description: patch.description } : {}),
    ...(patch.typeKey !== undefined ? { typeKey: patch.typeKey } : {}),
    ...(patch.statusKey !== undefined ? { statusKey: patch.statusKey } : {}),
    ...(patch.repoKey !== undefined ? { repoKey: patch.repoKey } : {}),
    ...(patch.priority !== undefined ? { priority: patch.priority } : {}),
    ...(patch.storyPoints !== undefined ? { storyPoints: patch.storyPoints } : {}),
    ...(patch.reviewerId !== undefined ? { reviewerId: patch.reviewerId } : {}),
    ...(patch.reporterId !== undefined ? { reporterId: patch.reporterId } : {}),
    ...(patch.teamIds !== undefined ? { teamIds: patch.teamIds } : {}),
    ...(patch.labelIds !== undefined ? { labelIds: patch.labelIds } : {}),
  };
}

function toInput(draft: Draft) {
  return {
    title: draft.title.trim(),
    description: draft.description,
    typeKey: draft.typeKey,
    statusKey: draft.statusKey,
    ...(draft.repoKey ? { repoKey: draft.repoKey } : {}),
    priority: draft.priority,
    storyPoints: draft.storyPoints,
    reviewerId: draft.reviewerId,
    ...(draft.reporterId ? { reporterId: draft.reporterId } : {}),
    ...(draft.labelIds.length ? { labelIds: draft.labelIds } : {}),
    ...(draft.teamIds ? { teamIds: draft.teamIds } : {}),
    parent: draft.parent,
  };
}

function toIssue(
  draft: Draft,
  meta: IssuesMeta,
  teams: IssuesMeta["teams"],
  parent: IssueRefInfo | null,
  createdAt: string,
  repoKey: string,
): IssueDetail {
  return {
    id: "draft",
    number: 0,
    identifier: "New",
    title: draft.title,
    description: draft.description,
    testingInstructions: "",
    typeKey: draft.typeKey,
    statusKey: draft.statusKey,
    repoKey,
    priority: draft.priority,
    storyPoints: draft.storyPoints,
    prUrl: null,
    previewRequired: null,
    release: null,
    sla: null,
    slaDueAt: null,
    slaStatus: "none",
    source: "web",
    parentId: parent?.id ?? null,
    taskId: null,
    currentAgentId: null,
    waitingOn: null,
    reporter: meta.users.find((user) => user.id === draft.reporterId) ?? null,
    reviewer: meta.users.find((user) => user.id === draft.reviewerId) ?? null,
    teams,
    labels: draft.labelIds.flatMap((id) => {
      const label = meta.labels.find((item) => item.id === id);
      return label ? [label] : [];
    }),
    parent,
    subIssues: { total: 0, done: 0 },
    startedAt: null,
    completedAt: null,
    cancelledAt: null,
    archivedAt: null,
    createdAt,
    updatedAt: createdAt,
    externalRef: null,
    relations: [],
    attachments: [],
    testSteps: [],
    children: [],
  };
}
